import React, { useState, useEffect } from 'react';
import { useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { BASE_URL } from './api';
import { useNavigate } from "react-router-dom";


const MedibuddyForm = () => {
  const { patientId } = useParams();
  const location = useLocation();
  const [bmrPdfFile, setBmrPdfFile] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const navigate = useNavigate();
  const hasFetchedSmartReport = useRef(false);

  
  const API_BASE_URL = BASE_URL;
  const [formData, setFormData] = useState({
    patient_unique_id: location.state?.unique_patient_id || '',
    bmr_pdf: null,
    vitals: {
      height: '',
      //weight: '',
      bmi: '',
      //body_fat: '',
      //visceral_fat: '',
      //base_metabolic_rate: '',
      //muscle_mass: '',
      //muscle_rate: '',
      //skeletal_muscle: '',
      //bone_mass: '',
      //body_water: '',
      waist_circumference: '',
      hip_circumference: '',
      //waist_hip_ratio: '',
      mid_arm_circumference: '',
      blood_grouping: '',
      body_temperature: '',
      oxygen_saturation: '',
      bp: '',
      blood_glucose: '',
      heart_rate: '',
      ecg_3_lead_1_channel: '',
      //random_blood_sugar: '',
      hemoglobin: '',
      acuity: '',
      color_blindness: '',
      ducochrome: '',
      astigmatism: '',
      hcv: '',
      hbsag: ''
    }
  });

  // Dropdown options
  const dropdownOptions = {
    acuity: ['Normal', 'Abnormal'],
    color_blindness: ['Normal', 'Red-Green', 'Blue-Yellow', 'Complete'],
    blood_grouping: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'],
    ducochrome: ['Normal', 'Abnormal'],
    astigmatism: ['Normal', 'Present'],
    ecg_3_lead_1_channel: ['Normal', 'Abnormal'],
    hcv: ['Negative', 'Positive'],
    hbsag: ['Negative', 'Positive'],
  };


  const fieldRanges = {
    waist_circumference: { min: 40, max: 200 },
    hip_circumference: { min: 50, max: 200 },
    mid_arm_circumference: { min: 10, max: 60 },
    hemoglobin: { min: 3, max: 25 },
    blood_glucose: { min: 20, max: 600 },
    oxygen_saturation: { min: 50, max: 100 },
    heart_rate: { min: 20, max: 220 },
    body_temperature: { min: 86, max: 113 },
  };



  const normalRanges = {
    hemoglobin: { normalMin: 12, normalMax: 17, criticalLow: 7, criticalHigh: 20 },
    blood_glucose: { normalMax: 140, criticalLow: 50, criticalHigh: 300 },
    oxygen_saturation: { normalMin: 95, criticalLow: 90 },
    heart_rate: { normalMin: 60, normalMax: 100, criticalLow: 40, criticalHigh: 130 },
    body_temperature: { normalMin: 97.7, normalMax: 99.5, criticalLow: 89.6, criticalHigh: 104 },
    mid_arm_circumference: { criticalLow: 18.5 },
    waist_circumference: {
      maleNormal: 90,
      femaleNormal: 80,
      maleCritical: 102,
      femaleCritical: 88
    }
  };





  const checkAbnormalVitals = () => {
    const vitals = formData.vitals;
    let abnormalMessages = [];
  
    // Hemoglobin
    const hb = parseFloat(vitals.hemoglobin);
    if (hb) {
      if (hb <= 7) abnormalMessages.push("⚠️ Severe anemia (Hb ≤ 7)");
      else if (hb >= 20) abnormalMessages.push("⚠️ Very high Hb (≥ 20)");
      else if (hb < 12 || hb > 17) abnormalMessages.push("⚠️ Hb not in normal range (12–17)");
    }
  
    // Blood Glucose
    const sugar = parseFloat(vitals.blood_glucose);
    if (sugar) {
      if (sugar <= 50) abnormalMessages.push("⚠️ Severe low sugar");
      else if (sugar >= 300) abnormalMessages.push("⚠️ Very high sugar");
      else if (sugar > 140) abnormalMessages.push("⚠️ Above normal sugar");
    }
  
    // Oxygen
    const spo2 = parseFloat(vitals.oxygen_saturation);
    if (spo2) {
      if (spo2 <= 85) abnormalMessages.push("🚨 Severe hypoxia");
      else if (spo2 <= 90) abnormalMessages.push("⚠️ Low oxygen");
      else if (spo2 < 95) abnormalMessages.push("⚠️ Below normal oxygen");
    }
  
    // Heart Rate
    const hr = parseFloat(vitals.heart_rate);
    if (hr) {
      if (hr <= 40) abnormalMessages.push("🚨 Severe low pulse");
      else if (hr >= 130) abnormalMessages.push("🚨 Very high pulse");
      else if (hr < 60 || hr > 100) abnormalMessages.push("⚠️ Abnormal pulse");
    }
  
    // Temperature
    const temp = parseFloat(vitals.body_temperature);
    if (temp) {
      if (temp <= 89.6) abnormalMessages.push("🚨 Severe hypothermia");
      else if (temp >= 104) abnormalMessages.push("🚨 High fever emergency");
      else if (temp < 97.7 || temp > 99.5) abnormalMessages.push("⚠️ Abnormal temperature");
    }
  
    // Mid Arm
    const mac = parseFloat(vitals.mid_arm_circumference);
    if (mac && mac <= 18.5) {
      abnormalMessages.push("⚠️ Low Mid Arm Circumference");
    }
  
    // If any abnormal → show confirm
    if (abnormalMessages.length > 0) {
      return window.confirm(
        abnormalMessages.join("\n") +
        "\n\nDo you still want to save?"
      );
    }
  
    return true;
  };



  const fetchSmartReport = async (uniqueId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/technician/smart-report/${uniqueId}/`
      );
  
      // ✅ If no data → silently skip (NO ERROR)
      if (!response.ok) {
        console.log("No smart report found");
        return;
      }
  
      const data = await response.json();
      console.log("SMART REPORT:", data);
  
      // ✅ Patient autofill
      if (data.patient) {
        setPatientData(data.patient);
      }
  
      // ✅ Vitals autofill (cleaned)
      if (data.vitals) {
        const cleanedVitals = cleanVitals(data.vitals);
  
        setFormData(prev => ({
          ...prev,
          vitals: mergeVitals(prev.vitals, cleanedVitals)
        }));
      }
  
    } catch (error) {
      console.log("Smart report skipped:", error.message);
    }
  };
  
  
  const cleanVitals = (vitals) => {
    const cleaned = {};
  
    Object.keys(vitals).forEach((key) => {
      const value = vitals[key];
  
      // ✅ convert unwanted values → blank
      if (
        value === null ||
        value === undefined ||
        value === 0 ||
        value === "0" ||
        value === "N/A" ||
        value?.toString().toUpperCase() === "NA"
      ) {
        cleaned[key] = '';
      } else {
        cleaned[key] = value;
      }
    });
  
    return cleaned;
  };



  useEffect(() => {
    let uniqueId = location.state?.unique_patient_id;
  
    if (!uniqueId && patientData?.unique_patient_id) {
      uniqueId = patientData.unique_patient_id;
    }
  
    // ✅ Prevent multiple calls
    if (uniqueId && !hasFetchedSmartReport.current) {
      hasFetchedSmartReport.current = true;
      fetchSmartReport(uniqueId);
    }
  
  }, [location.state, patientData]);

  const fetchPatientData = async () => {
    try {
      if (!patientId) {
        throw new Error('No patient ID provided');
      }

      const response = await fetch(`${API_BASE_URL}/api/campmanager/patients/${patientId}/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (!data.unique_patient_id) {
        throw new Error('Patient unique ID not found in response');
      }

      setPatientData(data);
      setFormData(prev => ({
        ...prev,
        patient_unique_id: data.unique_patient_id
      }));
    } catch (error) {
      console.error('Error fetching patient data:', error);
      alert('Error loading patient data: ' + error.message);
    }
  };

  const handleBmrPdfChange = (e) => {
    const file = e.target.files[0];
    setBmrPdfFile(file);
    setFormData(prev => ({
      ...prev,
      bmr_pdf: file
    }));
  };

  const handleVitalsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      vitals: {
        ...prev.vitals,
        [field]: value
      }
    }));
  };

const validateVitals = () => {
  const vitals = formData.vitals;

  for (let field in fieldRanges) {
    const value = vitals[field];

    // ✅ skip empty OR 0
    if (
      value === '' ||
      value === null ||
      value === undefined ||
      value === 0 ||
      value === "0"
    ) continue;

    const num = parseFloat(value);
    const { min, max } = fieldRanges[field];

    if (isNaN(num) || num < min || num > max) {
      alert(`${fieldLabels[field]} must be between ${min} and ${max}`);
      return false;
    }
  }

  // ✅ BP validation
  if (vitals.bp && vitals.bp !== "0") {
    const [systolic, diastolic] = vitals.bp.split('/').map(Number);

    if (
      !systolic || !diastolic ||
      systolic < 60 || systolic > 250 ||
      diastolic < 30 || diastolic > 150
    ) {
      alert("BP must be like 120/80 and within valid range");
      return false;
    }
  }

  return true;
};


const mergeVitals = (oldVitals, newVitals) => {
  const merged = { ...oldVitals };

  Object.keys(newVitals).forEach((key) => {
    if (
      !oldVitals[key] ||
      oldVitals[key] === '' ||
      oldVitals[key] === '0'
    ) {
      merged[key] = newVitals[key];
    }
  });

  return merged;
};



    const prepareVitalsData = () => {
      const vitals = formData.vitals;
      const updatedVitals = {};
    
      Object.keys(vitals).forEach((field) => {
        const value = vitals[field];
    
        if (
          value === '' ||
          value === null ||
          value === undefined
        ) {
          updatedVitals[field] = 0;   // ✅ Empty → 0
        } else {
          updatedVitals[field] = value;
        }
      });
    
      return updatedVitals;
    };
  


    const submitServiceStatus = async () => {
  try {
    const technicianId = localStorage.getItem("technicianId");

    const payload = {
      technician_id: Number(technicianId),
      patient_id: formData.patient_unique_id,
      service_ids: [8,13],
    };

    console.log("Submit Payload:", payload);

    const response = await fetch(
      `${API_BASE_URL}/api/technician/submit/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to submit services");
    }

    return data;

  } catch (error) {
    console.error("Service submit error:", error);
    throw error;
  }
};



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateVitals()) return;

    if (!checkAbnormalVitals()) return;

    try {
      if (!formData.patient_unique_id) {
        throw new Error('Patient Unique ID is required');
      }

      const formDataToSend = new FormData();
      formDataToSend.append('patient_unique_id', formData.patient_unique_id);

      if (bmrPdfFile) {
        formDataToSend.append('bmr_pdf', bmrPdfFile);
      }
     
      const finalVitals = prepareVitalsData(); 
      formDataToSend.append('vitals', JSON.stringify(finalVitals));

      const response = await fetch(`${API_BASE_URL}/api/technician/medibuddy/patient-data/`, {
        method: 'POST',
        body: formDataToSend,
      });

      const responseData = await response.json();

      if (response.ok) {
      
        // Medibuddy data save ho gaya
        console.log("Medibuddy saved");
      
        // Service complete API call
        await submitServiceStatus();
      
        alert('Medical service data submitted successfully!');
        navigate('/medivadi');
      
      } else {
        throw new Error(responseData.message || 'Failed to submit data');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(error.message || 'Failed to submit data. Please try again.');
    }
  };

  const fieldLabels = {
    height: 'Height',
    //weight: 'Weight',
    bmi: 'BMI',
    //body_fat: 'Body Fat',
    //visceral_fat: 'Visceral Fat',
    //base_metabolic_rate: 'Base Metabolic Rate',
    //muscle_mass: 'Muscle Mass',
    //muscle_rate: 'Muscle Rate',
    //skeletal_muscle: 'Skeletal Muscle',
    //bone_mass: 'Bone Mass',
    //body_water: 'Body Water',
    waist_circumference: 'Waist Circumference',
    hip_circumference: 'Hip Circumference',
    //waist_hip_ratio: 'Waist-Hip Ratio',
    mid_arm_circumference: 'Mid Arm Circumference',
    blood_grouping: 'Blood Grouping',
    body_temperature: 'Body Temperature',
    oxygen_saturation: 'Oxygen Saturation',
    bp: 'Blood Pressure',
    blood_glucose: 'Blood Glucose',
    heart_rate: 'Heart Rate',
    ecg_3_lead_1_channel: 'ECG (3 Lead 1 Channel)',
   // random_blood_sugar: 'Random Blood Sugar',
    hemoglobin: 'Hemoglobin',
    acuity: 'Acuity',
    color_blindness: 'Color Blindness',
    ducochrome: 'Ducochrome',
    astigmatism: 'Astigmatism',
    hcv: 'HCV',
    hbsag: 'HBsAg',
    
  };




  const handleThermalPrint = () => {
    const vitals = formData.vitals;
    const printContent = `
    <html>
    <head>
      <style>
        @media print {
          @page {
            size: 80mm auto;  /* 🔥 IMPORTANT (use 58mm if your printer is small) */
            margin: 0;
          }
    
          body {
            margin: 0;
            font-family: monospace;
          }
    
          .receipt {
            width: 100%;
            max-width: 80mm;
            font-size: 14px; /* 🔥 increase text size */
          }
    
          h3 {
            text-align: center;
            font-size: 16px;
            margin: 5px 0;
          }
    
          p {
            margin: 4px 0;
            font-size: 14px;
          }
    
          hr {
            border: none;
            border-top: 1px dashed black;
            margin: 6px 0;
          }
        }
      </style>
    </head>
    
    <body>
      <div class="receipt">
        <h3>Medical Report</h3>
        <hr/>
        <p><strong>Patient ID:</strong> ${formData.patient_unique_id}</p>
        ${patientData ? `<p><strong>Name:</strong> ${patientData.name}</p>` : ''}
        <hr/>
        ${Object.keys(vitals).map(field => `
          <p>${fieldLabels[field]}: ${vitals[field] || '-'}</p>
        `).join('')}
        <hr/>
        <p style="text-align:center;">Thank You</p>
      </div>
    </body>
    </html>
    `;
  
    const printWindow = window.open('', '', 'width=400,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };


  useEffect(() => {
    if (location.state?.autoFillVitals) {
      const { blood_pressure, heart_rate } = location.state.autoFillVitals;
      setFormData(prev => ({
        ...prev,
        vitals: {
          ...prev.vitals,
          bp: blood_pressure || prev.vitals.bp,
          heart_rate: heart_rate || prev.vitals.heart_rate
        }
      }));
    }
  }, [location.state]);
  
  

  if (!formData.patient_unique_id && !patientId) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-red-600">Error: No patient ID provided</h1>
          <button
            onClick={() => window.history.back()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
  <h1 className="text-2xl font-bold">Medical Service Form</h1>
  <button
    onClick={() => {
      console.log('patientData state:', patientData);
      console.log('location.state:', location.state);
      navigate("/health-monitor", { 
      state: { 
        patientName: location.state?.name || '',
        patientAge: location.state?.age || '',
        patientId: patientId,                        // ← ADD THIS
        unique_patient_id: location.state?.unique_patient_id || '' 
      } 
    })}}
    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
  >
    Go to Health Monitor
  </button>
</div>

{patientData && (
  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
    <h2 className="text-lg font-semibold">Patient Details</h2>
    <p>Name: {patientData.name}</p>
    <p>ID: {patientData.unique_patient_id}</p>
  </div>
)}

        <div className="space-y-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              BMR PDF Report
            </label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleBmrPdfChange}
              className="w-full p-2 border rounded-md"
            />
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Medical Measurements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.keys(formData.vitals).map((field) => (
                <div key={field} className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {fieldLabels[field]}
                    {fieldRanges[field] && (
                      <span className="text-xs text-gray-500">
                        {" "}({fieldRanges[field].min}-{fieldRanges[field].max})
                      </span>
                    )}
                  </label>
                  {dropdownOptions[field] ? (
                    <select
                      value={formData.vitals[field]}
                      onChange={(e) => handleVitalsChange(field, e.target.value)}
                      className="w-full p-2 border rounded-md bg-white"
                    >
                      <option value="">Select...</option>
                      {dropdownOptions[field].map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                     <input
                       type={fieldRanges[field] ? "number" : "text"}
                       value={formData.vitals[field]}
                       onChange={(e) => handleVitalsChange(field, e.target.value)}
                       className="w-full p-2 border rounded-md"
                       min={fieldRanges[field]?.min}
                       max={fieldRanges[field]?.max}
                     />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>


              <button
                type="button"
                onClick={handleThermalPrint}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Thermal Print
              </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedibuddyForm;